import { ADDITIONAL_SPACE_ON_SIDES, StationColor, X_SCALE_VALUE, Y_SCALE_VALUE } from 'constants/map';
import { BaseStationData, Station, TimeRange, StationStatus, StationType, ExpandedStation } from 'pages/Map/types';
import { Path } from '../../Path';
import { zIndex } from 'theme/zIndex';

/**
 * This BaseStation holds the common information (fields/methods) of all station types
 * All stations will extend this and can override any method defination specific to them if they needed
 */
export class BaseStation {
    id: string;
    name: string;
    description: string;
    time: TimeRange;
    xPosition: number;
    yPosition: number;
    parentStation?: Station;
    sectionID: string;
    abosluteXValue: number;
    abosluteYValue: number;
    magnificationLevel: number;

    constructor({ id, name, description, time, parentStation, xPosition, yPosition, sectionID, magnificationLevel }: BaseStationData) {
        this.id = id;
        this.name = name;
        this.description = description;
        this.time = time;
        this.parentStation = parentStation;
        this.xPosition = xPosition;
        this.yPosition = yPosition;
        this.sectionID = sectionID;
        this.abosluteXValue = this.getXValue();
        this.abosluteYValue = this.getYValue();
        this.magnificationLevel = magnificationLevel ?? -1; //-1 if no value provided, will be updated while setting relative value
    }

    getId() {
        return this.id;
    }

    isHidden(expandedStation?: ExpandedStation) {
        if (this.parentStation) {
            return !(expandedStation && this.getParentStation()?.getId() === expandedStation.getId());
        }

        return false;
    }

    getDescription() {
        return this.description;
    }

    getTimeline() {
        const min = this.time.weeks.min;
        const max = this.time.weeks.max;

        return max ? `${min} - ${max} weeks` : null;
    }

    getParentStation() {
        return this.parentStation;
    }

    /**
     * This method returns the relative xValue of station from zone start or subzone(if it is inside subzone)
     */
    getXValue(): number {
        return this.xPosition * X_SCALE_VALUE;
    }

    /**
     * This method returns the relative yValue of station from zone start or subzone(if it is inside subzone)
     */
    getYValue() {
        return this.yPosition * Y_SCALE_VALUE;
    }

    isOnLeftOf(station: NonNullable<ExpandedStation>) {
        return this.parentStation ? this.parentStation.xPosition < station.xPosition : this.xPosition < station.xPosition;
    }

    isHorizontallyCenterTo(station: NonNullable<ExpandedStation>) {
        return this.parentStation ? this.parentStation.xPosition === station.xPosition : this.xPosition === station.xPosition;
    }

    isOnRightOf(station: NonNullable<ExpandedStation>) {
        return this.parentStation ? this.parentStation.xPosition > station.xPosition : this.xPosition > station.xPosition;
    }

    isOnTopOf(station: NonNullable<ExpandedStation>) {
        return this.parentStation ? this.parentStation.yPosition < station.yPosition : this.yPosition < station.yPosition;
    }

    isOnBottomOf(station: NonNullable<ExpandedStation>) {
        return this.parentStation ? this.parentStation.yPosition > station.yPosition : this.yPosition > station.yPosition;
    }

    isVerticallyCenterTo(station: NonNullable<ExpandedStation>) {
        return this.parentStation ? this.parentStation.yPosition === station.yPosition : this.yPosition === station.yPosition;
    }

    /**
     * Whenever we close current expanded station / open another station. We need to update station positions accordingly without shifting current expanded station position
     * Ex: When we open subzone, subzone parent station remain in wherever we are clicking and it should expand to the right and left based on stations in it
     * Here we are adjusting positions of stations with previousExpandedStation width based on whether previousExpandedStation is right/left/top/top bottom of newly expanded station
     * Ex: If we have previous expanded station on left of newly expanded station, when we open new subzone we will close previous subzone, so previous subzone width will reduce and map has to shift to left.
     * But to avoid new subzone shift, adding previous subzone widths to all the stations on left side to fill the gap creted by closing previous subzone
     */
    setRelativePositionWithPreviousExpandedStation(expandedStation: ExpandedStation, previousExpandedStation: ExpandedStation) {
        if (!previousExpandedStation) {
            return;
        }

        /**
         * Adjusting positions when we close currently expanded station
         */
        if (!expandedStation) {
            this.setAbsoluteXValue(this.abosluteXValue - this.getRelativeXPositionWithExpandedStation(previousExpandedStation));
            this.setAbsoluteYValue(this.abosluteYValue - this.getRelativeYPositionWithExpandedStation(previousExpandedStation));
            return;
        }

        /**
         * Updates station positions based on whether previous expanaded station is on top/below of current expanded station
         */
        if (previousExpandedStation.isOnTopOf(expandedStation)) {
            if (this.isOnTopOf(previousExpandedStation) || this.isVerticallyCenterTo(previousExpandedStation)) {
                this.setAbsoluteYValue(this.abosluteYValue + previousExpandedStation.getHeight());
            }
        } else {
            if (this.isOnBottomOf(previousExpandedStation)) {
                this.setAbsoluteYValue(this.abosluteYValue - previousExpandedStation.getHeight());
            }
        }

        /**
         * Updates station positions based on whether previous expanaded station is on left/right/center of current expanded station
         */
        if (previousExpandedStation.isOnLeftOf(expandedStation)) {
            if (this.isOnLeftOf(previousExpandedStation)) {
                this.setAbsoluteXValue(this.abosluteXValue + previousExpandedStation.getWidth());
            } else if (this.isHorizontallyCenterTo(previousExpandedStation)) {
                this.setAbsoluteXValue(this.abosluteXValue + previousExpandedStation.getWidthOnRight());
            }
            return;
        }

        if (previousExpandedStation.isHorizontallyCenterTo(expandedStation)) {
            if (this.isOnLeftOf(previousExpandedStation)) {
                this.setAbsoluteXValue(this.abosluteXValue + previousExpandedStation.getWidthOnLeft());
            } else if (this.isOnRightOf(previousExpandedStation)) {
                this.setAbsoluteXValue(this.abosluteXValue - previousExpandedStation.getWidthOnRight());
            }
            return;
        }

        if (this.isOnRightOf(previousExpandedStation)) {
            this.setAbsoluteXValue(this.abosluteXValue - previousExpandedStation.getWidth());
        } else if (this.isHorizontallyCenterTo(previousExpandedStation)) {
            this.setAbsoluteXValue(this.abosluteXValue - previousExpandedStation.getWidthOnLeft());
        }
    }

    /**
     * Calculates the new xPosition of station based on expanded station
     */
    getRelativeXPositionWithExpandedStation(expandedStation: NonNullable<ExpandedStation>): number {
        if (this.parentStation) {
            return this.parentStation.getRelativeXPositionWithExpandedStation(expandedStation);
        }
        if (this.xPosition === expandedStation.xPosition) {
            return 0;
        }
        if (this.xPosition > expandedStation.xPosition) {
            return expandedStation.getWidthOnRight();
        }
        return -expandedStation.getWidthOnLeft();
    }

    /**
     * Calculates the new yPosition of station based on expanded station
     */
    getRelativeYPositionWithExpandedStation(expandedStation: ExpandedStation): number {
        if (this.parentStation) {
            return this.parentStation.getRelativeYPositionWithExpandedStation(expandedStation);
        }

        if (!expandedStation || expandedStation.getId() === this.getId()) {
            return 0;
        }
        if (this.yPosition > expandedStation.yPosition) {
            return expandedStation.getHeight();
        }
        return 0;
    }

    /**
     * Here we are adjsting positions of stations with expanded station positon to keep expanded parent station position in same place
     */
    setRelativePositionWithExpandedStation(expandedStation: ExpandedStation) {
        if (!expandedStation) {
            return;
        }
        this.setAbsoluteXValue(this.abosluteXValue + this.getRelativeXPositionWithExpandedStation(expandedStation));
        this.setAbsoluteYValue(this.abosluteYValue + this.getRelativeYPositionWithExpandedStation(expandedStation));
    }

    /**
     * Map data will have -xPositions, which results in -xValues. But map canvas uses draws map from 0,0
     * So adding -lowestXValueInMap to make xValue relative to 0
     */
    getIntitialAbsoluteXValue(lowestXValueInMap: number): number {
        if (this.parentStation) {
            const parentPosition = this.parentStation.getAbsoluteXValue();
            const childRelativePositionFromParent = this.getXValue();
            return parentPosition + childRelativePositionFromParent;
        }
        return this.getXValue() - lowestXValueInMap + ADDITIONAL_SPACE_ON_SIDES.left;
    }

    setAbsoluteXValue(abosluteXValue: number) {
        this.abosluteXValue = abosluteXValue;
    }

    getAbsoluteXValue(): number {
        return this.abosluteXValue;
    }

    getIntitialAbsoluteYValue(): number {
        if (this.parentStation?.getType() === StationType.SubZone) {
            const parentPosition = this.parentStation.getAbsoluteYValue();

            const childRelativePositionFromParent = this.getYValue();
            return parentPosition + childRelativePositionFromParent;
        }

        return this.getYValue() + ADDITIONAL_SPACE_ON_SIDES.top;
    }

    setAbsoluteYValue(abosluteYValue: number) {
        this.abosluteYValue = abosluteYValue;
    }

    getAbsoluteYValue(): number {
        return this.abosluteYValue;
    }

    /**
     * Stations aboslute positions will set initially and whenever there is change in expanded subzone
     * We will calcuale and set once and use whenever we need without calculating again
     */
    setAbsolutePosition(lowestXValueInMap: number) {
        const abosluteXValue = this.getIntitialAbsoluteXValue(lowestXValueInMap);
        const abosluteYValue = this.getIntitialAbsoluteYValue();
        this.setAbsoluteXValue(abosluteXValue);
        this.setAbsoluteYValue(abosluteYValue);
    }

    getAbsolutePosition() {
        return {
            x: this.getAbsoluteXValue(),
            y: this.getAbsoluteYValue()
        };
    }

    isSourceOrTargetForPath(path: Path) {
        return path.source.getId() === this.id || path.target.getId() === this.id;
    }

    /**
     * Station status depends paths connecting to it and currently active station, in the following order
     * If station is active station shown as active station
     * If atleast one travelled path connects station then considered travelled station
     * If all paths are marked as not travelled consideres as not travelled
     * Otherwise default
     */
    getStationStatus(activeStation: Station | undefined, paths: Path[], selectedStation?: Station) {
        if (activeStation?.getId() === this.id) {
            return StationStatus.ACTIVE_STATION;
        }

        if (this.isSelectedStation(selectedStation)) {
            return StationStatus.SELECTED;
        }

        const connectingPaths = paths.filter(path => this.isSourceOrTargetForPath(path));
        const hasTravelledPath = connectingPaths.some(path => path.isSelected);
        if (hasTravelledPath) {
            return StationStatus.TRAVELLED;
        }

        const isAllPathsMarkedAsNotTravelled = connectingPaths.length && connectingPaths.every(path => path.isSelected === false);
        if (isAllPathsMarkedAsNotTravelled) {
            return StationStatus.NOT_TRAVELLED;
        }

        return StationStatus.DEFAULT;
    }

    getStationColor = (status: StationStatus) => {
        return StationColor[status];
    };

    getStationInterasectionDirectionForSelectedPath = (path: Path) => {
        if (!this.isSourceOrTargetForPath(path)) {
            return null;
        }

        if (this.id === path.source.getId()) {
            return path.findSourceIntersectionDirection();
        }

        return path.findTargetIntersectionDirection();
    };

    getDefaultZIndex(activeStation: Station | undefined) {
        if (activeStation?.id === this.id) {
            return zIndex.ActiveStation;
        }

        return zIndex.Station;
    }

    getHoveredZIndex(currentIndex: number) {
        return Math.max(currentIndex, zIndex.ActiveStation) + 1;
    }

    getMagnificationLevel() {
        return this.magnificationLevel;
    }

    setMagnificationLevel(magnificationLevel: number) {
        return (this.magnificationLevel = magnificationLevel);
    }

    isSelectedStation(selectedStation?: Station) {
        return this.id === selectedStation?.id;
    }
}
